/**
 * Rivellum API client for configured read, submit, and faucet endpoints.
 */

const API_URL = import.meta.env.VITE_API_URL as string || 'https://api.rivellum.network';
const SUBMIT_URL = import.meta.env.VITE_SUBMIT_URL as string || 'https://submit.rivellum.network';
const FAUCET_URL = import.meta.env.VITE_FAUCET_URL as string || 'https://faucet.rivellum.network';

// ─── Types matching the real API responses ───────────────────────

export interface ChainInfo {
  chain_id: string;
  native_token: string;
  native_token_decimals: number;
}

export interface BalanceResponse {
  address: string;
  balance: string;
  balances: {
    native: {
      spendable: string;
      notarized: string;
    };
  };
  nonce: number;
  token: string;
  decimals: number;
}

export interface ApiParamType {
  kind: string;
  type: string;
}

export interface ApiParameter {
  name: string;
  optional: boolean;
  type: ApiParamType;
  description?: string;
}

export interface ApiAction {
  name: string;
  intentClass: string;
  parameters: ApiParameter[];
  description: string;
  visibility: string;
  mutates: boolean;
  returns?: string;
}

export interface ApiConfigProperty {
  title: string;
  type: string;
  description: string;
  uiWidget?: 'input' | 'number' | 'slider' | 'toggle' | 'select' | 'textarea' | 'assetPicker' | 'addressPicker' | 'table' | 'json';
  enum?: (string | number)[];
  enumLabels?: string[];
  default?: string | number | boolean;
  minimum?: number;
  maximum?: number;
}

export interface ApiConfigSchema {
  type: string;
  properties: Record<string, ApiConfigProperty>;
  required: string[];
}

export type ApiPrefabCategory = 'DEFI' | 'GAMING' | 'GOVERNANCE' | 'MONETIZATION' | 'PAYMENTS';

export interface ApiPrefab {
  id: string;
  name: string;
  category: ApiPrefabCategory;
  version: number;
  kind: string;
  sourceFile: string;
  sourceHash: string;
  microSafe: boolean;
  casualSafe: boolean;
  actions: ApiAction[];
  configSchema: ApiConfigSchema;
  description: string;
  tags: string[];
}

export interface PrefabsResponse {
  prefabs: ApiPrefab[];
}

export interface SubmitResponse {
  status: string;
  intent_id?: string;
  admission_id?: string;
  code_hash?: string;
}

export interface MistDeployResponse {
  status: string;
  code_hash: string;
  intent_id: string;
  contract_id?: string;
  fee_charged?: number;
  fee_display?: string;
}

export interface MistCompileResponse {
  bundle: string;
  code_hash: string;
  contract_name: string | null;
  bundle_size: number;
}

export interface MistFeeEstimateCategoryResponse {
  category: string;
  label: string;
  count: number;
  unit_cost_micro: string;
  total_cost_micro: string;
}

export interface MistFeeEstimateResponse {
  contract_name: string;
  max_fee_micro: string;
  max_fee_display: string;
  deploy_fee_micro: string;
  deploy_fee_display: string;
  storage_fee_micro: string;
  deploy_complexity: 'Simple' | 'Medium' | 'Complex' | null;
  total_fee_micro: string;
  total_fee_display: string;
  rivl_fee: string;
  rivl_fee_display: string;
  twap_usd_per_rivl: string;
  functions: {
    function_name: string;
    fee_micro: string;
    fee_display: string;
    category_count: number;
    categories: MistFeeEstimateCategoryResponse[];
  }[];
}

export interface ProjectRegisterResponse {
  project_id: string;
  modules: ProjectModuleResponse[];
  status: string;
  intent_id: string;
}

export interface ProjectModuleResponse {
  prefab_id: string;
  label: string;
  code_hash: string;
  intent_id: string;
  contract_id?: string;
}

export interface AddModuleResponse {
  project_id: string;
  module: ProjectModuleResponse;
  status: string;
  intent_id: string;
}

export interface ProjectPrefabEntry {
  prefab_id: string;
  label: string;
  init_args: unknown[];
}

// ─── API Functions ───────────────────────────────────────────────

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET ${url} failed (${res.status}): ${text}`);
  }
  return res.json();
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // Try to extract a readable detail from JSON error responses
    try {
      const errJson = JSON.parse(text);
      if (errJson.detail) throw new Error(errJson.detail);
    } catch (e) {
      if (e instanceof Error && !e.message.startsWith('POST') && !e.message.startsWith('Unexpected'))
        throw e;
    }
    throw new Error(`POST ${url} failed (${res.status}): ${text}`);
  }
  return res.json();
}

export const rivellumApi = {
  /** GET /health */
  health: () => get<{ status: string; version: string }>(`${API_URL}/health`),

  /** GET /v1/info */
  getChainInfo: () => get<ChainInfo>(`${API_URL}/v1/info`),

  /** GET /prefabs — all registered prefab definitions */
  getPrefabs: () => get<PrefabsResponse>(`${API_URL}/prefabs`),

  /** GET /prefabs/:id — single prefab */
  getPrefab: (id: string) => get<ApiPrefab>(`${API_URL}/prefabs/${encodeURIComponent(id)}`),

  /** GET /v1/balance/:address */
  getBalance: (address: string) => get<BalanceResponse>(`${API_URL}/v1/balance/${encodeURIComponent(address)}`),

  /** GET /v1/nonce/:address */
  getNonce: (address: string) => get<{ nonce: number }>(`${API_URL}/v1/nonce/${encodeURIComponent(address)}`),

  /** POST /v1/submit_envelope — submit an encrypted envelope */
  submitEnvelope: (payload: unknown) => post<SubmitResponse>(`${SUBMIT_URL}/v1/submit_envelope`, payload),
  /** Backward-compatible alias */
  submitIntent: (payload: unknown) => post<SubmitResponse>(`${SUBMIT_URL}/v1/submit_envelope`, payload),

  /** POST /v1/mist/deploy — deploy a Mist contract */
  deployMist: (payload: unknown) => post<MistDeployResponse>(`${SUBMIT_URL}/v1/mist/deploy`, payload),

  /** POST /v1/mist/compile — compile Mist source to a deployable bundle */
  compileMist: (source: string) => post<MistCompileResponse>(`${SUBMIT_URL}/v1/mist/compile`, { source }),

  /** POST /v1/mist/estimate-fee — estimate fees for deploying Mist source */
  estimateFee: (source: string) => post<MistFeeEstimateResponse>(
    `${SUBMIT_URL}/v1/mist/estimate-fee`,
    { source },
  ),

  /** POST /v1/prefab/deploy — compile + deploy a single prefab with init_args */
  deployPrefab: (prefabId: string, initArgs: unknown[]) =>
    post<MistDeployResponse>(`${SUBMIT_URL}/v1/prefab/deploy`, {
      prefab_id: prefabId,
      init_args: initArgs,
    }),

  /** POST /v1/project/register — register a project with multiple prefabs */
  registerProject: (name: string, prefabs: ProjectPrefabEntry[], sender?: string) =>
    post<ProjectRegisterResponse>(`${SUBMIT_URL}/v1/project/register`, {
      name,
      prefabs,
      sender: sender ?? '',
    }),

  /** POST /v1/project/add_module — register a single prefab to an existing project */
  registerPrefab: (projectId: string, projectName: string, prefabId: string, label: string, initArgs: unknown[] = [], sender?: string) =>
    post<AddModuleResponse>(`${SUBMIT_URL}/v1/project/add_module`, {
      project_id: projectId,
      project_name: projectName,
      prefab_id: prefabId,
      label,
      init_args: initArgs,
      sender: sender ?? '',
    }),

  /** POST /v1/faucet/mint — request tokens when a network and faucet are available */
  faucetMint: (address: string, amount?: number) =>
    post<{ status: string }>(`${FAUCET_URL}/v1/faucet/mint`, { address, amount: amount ?? 1_000_000_000 }),
};
